/**
 * React hook for the process-wide /ws/signals client.
 * Socket ownership lives in signalWebSocketClient — not in effect cleanup.
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  mapWebSocketSignalToSignal,
  WebSocketSignalPayload,
} from "../services/signalMapper";
import { Signal } from "../views/Dashboard/Tables/components/SignalCard";
import {
  SystemPerformanceData,
  AccountUpdateData,
  TradeFplUpdateItem,
} from "../types/signals";
import type { MarketOverviewLatest } from "../types/marketOverview";
import { dispatchReportReady } from "../utils/reportReadyEvents";
import {
  signalWebSocketClient,
  type SignalWebSocketClientStatus,
} from "../services/signalWebSocketClient";
import { isDemoEntryAnalysisRoute } from "../utils/demoEntryAnalysisSession";

interface UseSignalWebSocketOptions {
  onSignalReceived?: (signal: Signal) => void;
  onNoSignalReceived?: (reason: string, eventData: any) => void;
  onArbiterPerformance?: (data: SystemPerformanceData, timestamp?: string) => void;
  onAccountUpdate?: (data: AccountUpdateData, timestamp?: string) => void;
  onTradeFplUpdate?: (updates: TradeFplUpdateItem[], timestamp?: string) => void;
  onTradeClosed?: (tradeIds: string[], timestamp?: string) => void;
  onMarketOverview?: (data: MarketOverviewLatest) => void;
  onNotification?: (row: any, timestamp?: string) => void;
  /** Clerk has finished loading. */
  authLoaded?: boolean;
  /** User is signed in (only meaningful when authLoaded). */
  signedIn?: boolean;
  websocketPath?: string | null;
  getAuthToken?: () => Promise<string | null>;
}

export const useSignalWebSocket = (options: UseSignalWebSocketOptions = {}) => {
  const location = useLocation();
  const isDemoRoute = isDemoEntryAnalysisRoute(location.pathname);
  const {
    onSignalReceived,
    onNoSignalReceived,
    onArbiterPerformance,
    onAccountUpdate,
    onTradeFplUpdate,
    onTradeClosed,
    onMarketOverview,
    onNotification,
    authLoaded = false,
    signedIn = false,
    websocketPath = null,
    getAuthToken = null,
  } = options;

  const [status, setStatus] = useState<SignalWebSocketClientStatus>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });

  const onSignalReceivedRef = useRef(onSignalReceived);
  const onNoSignalReceivedRef = useRef(onNoSignalReceived);
  const onArbiterPerformanceRef = useRef(onArbiterPerformance);
  const onAccountUpdateRef = useRef(onAccountUpdate);
  const onTradeFplUpdateRef = useRef(onTradeFplUpdate);
  const onTradeClosedRef = useRef(onTradeClosed);
  const onMarketOverviewRef = useRef(onMarketOverview);
  const onNotificationRef = useRef(onNotification);

  useEffect(() => {
    onSignalReceivedRef.current = onSignalReceived;
  }, [onSignalReceived]);
  useEffect(() => {
    onNoSignalReceivedRef.current = onNoSignalReceived;
  }, [onNoSignalReceived]);
  useEffect(() => {
    onArbiterPerformanceRef.current = onArbiterPerformance;
  }, [onArbiterPerformance]);
  useEffect(() => {
    onAccountUpdateRef.current = onAccountUpdate;
  }, [onAccountUpdate]);
  useEffect(() => {
    onTradeFplUpdateRef.current = onTradeFplUpdate;
  }, [onTradeFplUpdate]);
  useEffect(() => {
    onTradeClosedRef.current = onTradeClosed;
  }, [onTradeClosed]);
  useEffect(() => {
    onMarketOverviewRef.current = onMarketOverview;
  }, [onMarketOverview]);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    signalWebSocketClient.configure({
      getAuthToken: getAuthToken ?? undefined,
      websocketPath,
    });
  }, [getAuthToken, websocketPath]);

  // Only touch the socket once Clerk is loaded — never deactivate during hydration.
  // Demo Entry Analysis is public/poll-based; do not open /ws/signals on that funnel.
  useEffect(() => {
    if (!authLoaded) return;
    if (signedIn && !isDemoRoute) {
      signalWebSocketClient.activateSession();
    } else {
      signalWebSocketClient.deactivateSession();
    }
  }, [authLoaded, isDemoRoute, signedIn]);

  useEffect(() => {
    return signalWebSocketClient.subscribeStatus(setStatus);
  }, []);

  const signedInRef = useRef(signedIn);
  useEffect(() => {
    signedInRef.current = signedIn;
  }, [signedIn]);

  useEffect(() => {
    return signalWebSocketClient.subscribeMessages((message) => {
      // Ignore deliverable notifications after sign-out / session expiry.
      if (!signedInRef.current) return;

      const typed = message as { type?: string; data?: any; timestamp?: string };

      if (typed.type === "report_ready") {
        const reportId = typed.data?.report_id ? String(typed.data.report_id) : "";
        const reportStatus = typed.data?.status ? String(typed.data.status) : "";
        const message =
          typeof typed.data?.message === "string" ? typed.data.message : undefined;
        const action =
          typeof typed.data?.action === "string" ? typed.data.action : undefined;
        if (reportId) {
          dispatchReportReady({ reportId, status: reportStatus, message, action });
        }
        return;
      }

      if (typed.type === "notification") {
        const row = typed.data;
        const ts = typed.timestamp;
        try {
          onNotificationRef.current?.(row, ts);
        } catch (error) {
          console.error("[signals] WS onNotification handler failed:", error);
        }
        const eventKey = row?.event_key ?? row?.eventKey;
        const category = row?.category;
        const dataJson = row?.data_json ?? row?.dataJson;
        if (
          category === "reports" &&
          (eventKey === "report_ready" || eventKey === "report_failed") &&
          dataJson?.report_id
        ) {
          const pushMessage =
            typeof dataJson.message === "string" ? dataJson.message : undefined;
          const pushAction =
            typeof dataJson.action === "string" ? dataJson.action : undefined;
          dispatchReportReady({
            reportId: String(dataJson.report_id),
            status: String(dataJson.status ?? (eventKey === "report_failed" ? "failed" : "published")),
            message: pushMessage,
            action: pushAction,
          });
        }
        try {
          window.dispatchEvent(
            new CustomEvent("notifications:ws", { detail: { row, timestamp: ts } })
          );
        } catch {
          // ignore
        }
        return;
      }

      if (typed.type === "trade_request" && onSignalReceivedRef.current) {
        try {
          const signal = mapWebSocketSignalToSignal(message as WebSocketSignalPayload);
          onSignalReceivedRef.current(signal);
        } catch (error) {
          console.error("[signals] Failed to map trade_request:", error);
        }
        return;
      }

      if (typed.type === "no_signal" && onNoSignalReceivedRef.current) {
        const reason = typed.data?.reason ?? typed.data?.payload?.reason ?? "";
        if (reason) {
          onNoSignalReceivedRef.current(reason, typed.data);
        }
        return;
      }

      if (typed.type === "system_performance" && onArbiterPerformanceRef.current && typed.data) {
        onArbiterPerformanceRef.current(typed.data, typed.timestamp);
        return;
      }

      if (typed.type === "account_update" && onAccountUpdateRef.current && typed.data) {
        const data = typed.data as AccountUpdateData & { account_code?: string };
        const hasMetrics =
          typeof data.unrealized_pnl === "number" || data.balance != null;
        const hasSlTpUpdates =
          data.sl_tp_updates && Array.isArray(data.sl_tp_updates) && data.sl_tp_updates.length > 0;
        if (hasMetrics || hasSlTpUpdates) {
          onAccountUpdateRef.current(data, typed.timestamp);
        }
        return;
      }

      if (
        (typed.type === "trade_fpl_update" || typed.type === "trade_fpl_updates") &&
        onTradeFplUpdateRef.current &&
        Array.isArray(typed.data) &&
        typed.data.length > 0
      ) {
        onTradeFplUpdateRef.current(typed.data, typed.timestamp);
        return;
      }

      if (typed.type === "trade_closed" && onTradeClosedRef.current && typed.data?.trade_ids) {
        onTradeClosedRef.current(typed.data.trade_ids, typed.timestamp);
        return;
      }

      if (typed.type === "market_overview" && onMarketOverviewRef.current && typed.data?.instrument) {
        onMarketOverviewRef.current(typed.data);
        return;
      }

      if (typed.type === "error") {
        console.error("[signals] WS error message:", typed.data?.message || typed.data?.error);
      }
    });
  }, []);

  const connect = useCallback(() => {
    signalWebSocketClient.reconnect();
  }, []);

  const disconnect = useCallback(() => {
    signalWebSocketClient.deactivateSession();
  }, []);

  return {
    isConnected: status.isConnected,
    isConnecting: status.isConnecting,
    error: status.error,
    connect,
    disconnect,
  };
};
